import { useEffect, useMemo } from 'react';
import L from 'leaflet';
import { Circle, CircleMarker, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import { formatDateTime, type Device, type Location } from '@findmy/shared';
import { isApproximate } from '../lib/history';
import { markerIcon } from './markerIcon';
import { fitVisible, flyToVisible, outsideVisible } from './viewport';

const ROUTE = '#2563eb';

/**
 * A device's route: a line through its precise fixes, start and end dots, faint rings
 * for approximate (IP-based) fixes when shown, and the device itself at the point the
 * timeline is on.
 */
export function HistoryLayer({
  device,
  points,
  current,
  fitKey,
}: {
  device: Device;
  points: Location[];
  current: Location | null;
  /** Changes when a new range has loaded; the map is framed to the route then. */
  fitKey: string | null;
}) {
  const map = useMap();
  const line = useMemo(
    () => points.filter((p) => !isApproximate(p)).map((p) => [p.lat, p.lon] as [number, number]),
    [points],
  );
  const approximate = useMemo(() => points.filter(isApproximate), [points]);
  const icon = useMemo(
    () => markerIcon({ id: device.id, name: device.name, type: device.type, state: 'fresh', selected: true }),
    [device.id, device.name, device.type],
  );

  useEffect(() => {
    if (!fitKey || points.length === 0) return;
    const precise = points.filter((p) => !isApproximate(p));
    fitVisible(
      map,
      (precise.length > 0 ? precise : points).map((p) => L.latLng(p.lat, p.lon)),
    );
  }, [fitKey, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scrubbing the timeline pans only when the device would leave the visible area.
  useEffect(() => {
    if (!current) return;
    const target = L.latLng(current.lat, current.lon);
    if (outsideVisible(map, target)) flyToVisible(map, target, map.getZoom());
  }, [map, current?.lat, current?.lon]); // eslint-disable-line react-hooks/exhaustive-deps

  const first = line[0];
  const last = line.at(-1);
  return (
    <>
      {line.length > 1 && (
        <Polyline
          positions={line}
          pathOptions={{ color: ROUTE, weight: 4, opacity: 0.7 }}
          interactive={false}
        />
      )}
      {approximate.map((p) => (
        <Circle
          key={p.id}
          center={[p.lat, p.lon]}
          radius={p.accuracyM}
          pathOptions={{ color: '#64748b', weight: 1, fillOpacity: 0.04, dashArray: '6 6' }}
          interactive={false}
        />
      ))}
      {first && (
        <CircleMarker
          center={first}
          radius={6}
          pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#16a34a', fillOpacity: 1 }}
        >
          <Tooltip>Start: {formatDateTime(points.find((p) => !isApproximate(p))?.recordedAt ?? 0)}</Tooltip>
        </CircleMarker>
      )}
      {last && line.length > 1 && (
        <CircleMarker
          center={last}
          radius={6}
          pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#dc2626', fillOpacity: 1 }}
        >
          <Tooltip>End: {formatDateTime(points.findLast((p) => !isApproximate(p))?.recordedAt ?? 0)}</Tooltip>
        </CircleMarker>
      )}
      {current && (
        <>
          <Circle
            center={[current.lat, current.lon]}
            radius={current.accuracyM}
            pathOptions={{
              color: ROUTE,
              weight: 1,
              fillOpacity: 0.12,
              dashArray: isApproximate(current) ? '6 6' : undefined,
            }}
            interactive={false}
          />
          <Marker
            position={[current.lat, current.lon]}
            icon={icon}
            title={`${device.name} at ${formatDateTime(current.recordedAt)}`}
            alt={device.name}
            zIndexOffset={1000}
            interactive={false}
          />
        </>
      )}
    </>
  );
}
